import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from 'react';

import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Stack,
  Typography,
} from '@mui/material';
import { useFileInput } from '@uppy/react';
import { Magnifier } from '@vanyapr/react-image-magnifiers';
import { useTranslation } from 'react-i18next';

import styles from './AttachmentFilePreviewer.module.scss';
import { AllowedFileTypes, type FilePreview } from './types';
import { convertImageFileToScaledVersions } from './utils';
import { convertPdfFileToImage } from './utils';
import { FileAttachmentModel } from '../../types';
import { useGetBlobFileFromStorage } from '@/api/REST/blobFile';
import { useDeleteExpenseAttachment } from '@/api/REST/expenseAttachment';
import { Icon } from '@/components/ui/Icon';
import { IconButton } from '@/components/ui/IconButton';
import { uiIcons } from '@/constants/uiIcons';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { ExpenseFormType } from '@/pages/register/expenses/types';

interface AttachmentFilePreviewerProps {
  fileAttachments: FileAttachmentModel[];
  expenseId: number | null;
  expenseType: ExpenseFormType;
  isExpenseLocked?: boolean;
  onAdditionalFilesAdded: (files: File[]) => void;
  onFileAttachmentDeleted: (deletedFileUrl: string) => void;
}

export const AttachmentFilePreviewer = ({
  fileAttachments,
  expenseId,
  expenseType,
  isExpenseLocked = false,
  onAdditionalFilesAdded,
  onFileAttachmentDeleted,
}: AttachmentFilePreviewerProps) => {
  const { t } = useTranslation('expenses');
  const { getInputProps } = useFileInput();
  const { showConfirm } = useConfirmDialog();
  const deleteExpenseAttachment = useDeleteExpenseAttachment();

  const additionalFilesInputEl = useRef<HTMLInputElement>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const currentlyProcessingFilePreviewsRef = useRef<Set<FileAttachmentModel>>(
    new Set(),
  );
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);
  const createdObjectUrlsRef = useRef<string[]>([]); // For cleanup

  const [currentFileIndex, setCurrentFileIndex] = useState<number>(0);

  const [fileAttachmentToFetch, setFileAttachmentToFetch] =
    useState<FileAttachmentModel | null>(null);
  const [fetchFileAttachmentEnabled, setFetchFileAttachmentEnabled] =
    useState<boolean>(false);

  const {
    data: downloadBlob,
    error: downloadError,
    isLoading: isDownloading,
  } = useGetBlobFileFromStorage(
    fileAttachmentToFetch?.signedUrl || null,
    fetchFileAttachmentEnabled, // Don't auto-fetch, only when we trigger download
  );

  // Cleanup all Object URLs when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup converted image URLs
      createdObjectUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
    };
  }, []);

  useEffect(() => {
    const processFiles = async () => {
      if (fileAttachments.length === 0) {
        setFilePreviews([]);
        return;
      }

      const newFileAttachments: FileAttachmentModel[] = fileAttachments.filter(
        (attachment) => {
          return (
            !filePreviews.some(
              (preview) => preview.databaseUrl === attachment.databaseUrl,
            ) && !currentlyProcessingFilePreviewsRef.current.has(attachment)
          );
        },
      );

      if (newFileAttachments.length === 0) {
        return; // No new files to process
      }

      const newFilePreviews: FilePreview[] = [];

      for (const fileAttachment of newFileAttachments) {
        currentlyProcessingFilePreviewsRef.current.add(fileAttachment);
        const file = fileAttachment.file;

        if (!file || fileAttachment.isError || !fileAttachment.databaseUrl) {
          newFilePreviews.push({
            pages: null,
            databaseUrl: fileAttachment.databaseUrl ?? null,
            isError: true,
            fileType: (file?.type as AllowedFileTypes) ?? null,
          });
          currentlyProcessingFilePreviewsRef.current.delete(fileAttachment);
          continue; // skip to next file
        }

        if (file.type === 'application/pdf') {
          try {
            const filePreviewObjectUrls = await convertPdfFileToImage(file);
            createdObjectUrlsRef.current.push(
              ...filePreviewObjectUrls.flatMap((page) => [
                page.defaultResObjectUrl,
                page.highResObjectUrl,
              ]),
            );
            newFilePreviews.push({
              pages: filePreviewObjectUrls,
              databaseUrl: fileAttachment.databaseUrl,
              fileType: file.type,
            });
          } catch (error) {
            console.error('Error processing file:', error);
            newFilePreviews.push({
              pages: null,
              databaseUrl: fileAttachment.databaseUrl,
              isError: true,
              fileType: 'application/pdf',
            });
          } finally {
            currentlyProcessingFilePreviewsRef.current.delete(fileAttachment);
          }
        } else {
          try {
            const scaledImageUrls =
              await convertImageFileToScaledVersions(file);
            createdObjectUrlsRef.current.push(
              ...Object.values(scaledImageUrls),
            );
            newFilePreviews.push({
              pages: [scaledImageUrls],
              databaseUrl: fileAttachment.databaseUrl,
              fileType: file.type as 'image/png' | 'image/jpeg' | 'image/webp',
            });
          } catch (error) {
            console.error('Error processing image file:', error);
            // Fallback to simple URL creation if scaling fails
            const imgUrl = URL.createObjectURL(file);
            createdObjectUrlsRef.current.push(imgUrl);
            newFilePreviews.push({
              pages: [
                {
                  defaultResObjectUrl: imgUrl,
                  highResObjectUrl: imgUrl,
                },
              ],
              databaseUrl: fileAttachment.databaseUrl,
              fileType: file.type as 'image/png' | 'image/jpeg' | 'image/webp',
            });
          } finally {
            currentlyProcessingFilePreviewsRef.current.delete(fileAttachment);
          }
        }
      }

      setFilePreviews((prevPreviews) => [...prevPreviews, ...newFilePreviews]);
    };

    processFiles();
  }, [fileAttachments.length]);

  const handleFileChange = (_event: ChangeEvent<unknown>, value: number) => {
    setCurrentFileIndex(value - 1);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const triggerFileDownload = async (fileDatabaseUrl: string) => {
    // Find the file attachment that matches the database URL
    const fileAttachment = fileAttachments.find(
      (attachment) => attachment.databaseUrl === fileDatabaseUrl,
    );

    if (!fileAttachment?.signedUrl) {
      console.error('No signed URL found for file:', fileDatabaseUrl);
      return;
    }

    setFileAttachmentToFetch(fileAttachment);
    setFetchFileAttachmentEnabled(true);
  };

  // trigger download when blob is fetched from signed URL
  useEffect(() => {
    if (downloadError) {
      console.error('Error downloading file:', downloadError);
      setFetchFileAttachmentEnabled(false);
      setFileAttachmentToFetch(null);
      return;
    }

    if (!isDownloading && downloadBlob && fileAttachmentToFetch) {
      const blob = downloadBlob;

      // Create object URL from blob
      const blobUrl = URL.createObjectURL(blob);

      // Create temporary anchor element for download
      const link = document.createElement('a');
      link.style.display = 'none'; // Hide the element
      link.href = blobUrl;
      link.download =
        fileAttachmentToFetch.databaseUrl?.split('/').pop() || 'download';
      document.body.appendChild(link);

      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        link.click();

        // Clean up after a short delay
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(blobUrl);
          setFileAttachmentToFetch(null);
          setFetchFileAttachmentEnabled(false);
          handleCloseMenu();
        }, 100);
      });
    }
  }, [downloadBlob, isDownloading, downloadError, fetchFileAttachmentEnabled]);

  const handleFileDeletion = async (fileDatabaseUrl: string) => {
    const { confirmed } = await showConfirm({
      variant: 'delete',
    });

    if (confirmed) {
      try {
        await deleteExpenseAttachment.mutateAsync({
          attachmentUrl: fileDatabaseUrl,
          expenseId: expenseId || undefined,
          expenseType: expenseType,
        });
        onFileAttachmentDeleted(fileDatabaseUrl);
        setFilePreviews((prevPreviews) =>
          prevPreviews.filter(
            (preview) => preview.databaseUrl !== fileDatabaseUrl,
          ),
        );
        handleCloseMenu();
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    }
  };

  useEffect(() => {
    setCurrentFileIndex((i) =>
      filePreviews.length === 0 ? 0 : Math.min(i, filePreviews.length - 1),
    );
  }, [filePreviews.length]);

  const handleUploadAdditionalFilesClick = () => {
    additionalFilesInputEl.current?.click();
    handleCloseMenu();
  };

  const handleAdditionalFilesUpload = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      onAdditionalFilesAdded(fileArray);
      // Clear the input value to allow re-uploading the same file if needed
      event.target.value = '';
    }
  };

  return (
    <>
      <Stack
        className={styles.AttachmentFilePreviewWrapper}
        alignItems="center"
        justifyContent="space-between"
      >
        {filePreviews.length > 0 && filePreviews[currentFileIndex] && (
          <>
            <div className={styles.Actions}>
              <IconButton
                id={`file-actions-button`}
                iconName={uiIcons.controls.moreOptionsHorizontal}
                tooltip={t('filePreview.FileActionsButtonTooltip')}
                variant="outlined"
                size="small"
                aria-controls={menuAnchorEl ? 'file-actions-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={menuAnchorEl ? 'true' : undefined}
                onClick={(e) => handleMenuClick(e)}
                data-automation-id="expense-attachment-actions-button"
              />
            </div>

            <div className={styles.FilePreviewContainer}>
              {filePreviews[currentFileIndex]?.isError ? (
                <Stack
                  className={styles.ErrorMessageWrapper}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon name={uiIcons.status.error} size={64} color="error" />
                  <Typography variant="bodySemiBold" color="error">
                    {filePreviews[currentFileIndex]?.databaseUrl
                      ? t('filePreview.PreviewGenerationError') // on error with database URL, the file exists but preview generation failed
                      : t('filePreview.FallbackError')}
                  </Typography>
                </Stack>
              ) : (
                filePreviews[currentFileIndex]?.pages.map((page, index) => (
                  <Magnifier
                    key={`preview-${currentFileIndex}-${index}`}
                    imageSrc={page.defaultResObjectUrl}
                    largeImageSrc={page.highResObjectUrl}
                    imageAlt={`${filePreviews[currentFileIndex]?.databaseUrl?.split('/')?.pop() || 'file_preview'}_${index}`}
                    dragToMove
                    cursorStyle="zoom-in"
                  />
                ))
              )}
            </div>
            <Pagination
              className={styles.Pagination}
              count={filePreviews.length}
              page={currentFileIndex + 1}
              onChange={handleFileChange}
            />
          </>
        )}
      </Stack>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        autoFocus={false}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        slotProps={{
          list: {
            'aria-labelledby': menuAnchorEl ? 'file-actions-button' : undefined,
          },
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {!isExpenseLocked && (
          <MenuItem onClick={handleUploadAdditionalFilesClick}>
            <ListItemIcon>
              <Icon name={uiIcons.file.attach} size="small" />
            </ListItemIcon>
            <ListItemText>
              {t('filePreview.UploadAdditionalMenuItem')}
            </ListItemText>
          </MenuItem>
        )}
        {!filePreviews[currentFileIndex]?.isError &&
          filePreviews[currentFileIndex]?.databaseUrl &&
          // Array as Menu does not allow for fragment children
          [
            <MenuItem
              key="download"
              onClick={() =>
                triggerFileDownload(filePreviews[currentFileIndex].databaseUrl!)
              }
            >
              <ListItemIcon>
                <Icon name={uiIcons.actions.download} size="small" />
              </ListItemIcon>
              <ListItemText> {t('filePreview.DownloadMenuLabel')}</ListItemText>
            </MenuItem>,
            !isExpenseLocked && (
              <MenuItem
                key="delete"
                onClick={() =>
                  handleFileDeletion(
                    filePreviews[currentFileIndex].databaseUrl!,
                  )
                }
              >
                <ListItemIcon>
                  <Icon name={uiIcons.actions.delete} size="small" />
                </ListItemIcon>
                <ListItemText>{t('filePreview.DeleteMenuLabel')}</ListItemText>
              </MenuItem>
            ),
          ].filter(Boolean)}
      </Menu>

      {/* Hidden file input to trigger from additional file upload menu item */}
      <input
        className={styles.HiddenFileInput}
        ref={additionalFilesInputEl}
        {...getInputProps()}
        onChange={handleAdditionalFilesUpload}
      />
    </>
  );
};
